import type { Entity, Like } from '~/types/plugins/like';

export default defineNuxtPlugin({
  name: 'like-plugin',
  dependsOn: ['gtm-plugin'],
  async setup() {
    const { $api, $snackbar, $auth, $i18n,$customGtm } = useNuxtApp();
    const { t: $t } = $i18n;
    const like: Like = reactive({
      likedEntities: {
        tracks: [],
        playlists: [],
        albums: [],
        artists: [],
        games: [],
        videos: [],
        'Online Game': [],
        videoCollection: [],
        podcast: [],
        podcastCollection: [],
        audiobook: [],
        audiobookChapter: [],
        CloudGame: [],
        product: []
      },
      entityMap: {
        Track: 'tracks',
        Playlist: 'playlists',
        Artist: 'artists',
        Game: 'games',
        'Online Game': 'Online Game',
        Video: 'videos',
        Album: 'albums',
        'Video Collection': 'videoCollection',
        'Podcast Collection': 'podcastCollection',
        Podcast: 'podcast',
        'Audiobook Chapter': 'audiobookChapter',
        Audiobook: 'audiobook',
        CloudGame: 'CloudGame',
        product: 'product'
      },
      removed: null,
      tracks: computed(() => {
        if (!like.likedEntities?.tracks) {
          return {};
        }
        return like.likedEntities.tracks.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      playlists: computed(() => {
        if (!like.likedEntities?.playlists) {
          return {};
        }
        return like.likedEntities.playlists.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      albums: computed(() => {
        if (!like.likedEntities?.albums) {
          return {};
        }
        return like.likedEntities.albums.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      artists: computed(() => {
        if (!like.likedEntities?.artists) {
          return {};
        }
        return like.likedEntities.artists.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      games: computed(() => {
        if (!like.likedEntities?.games) {
          return {};
        }
        return like.likedEntities.games.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      product: computed(() => {
        if (!like.likedEntities?.product) {
          return {};
        }
        return like.likedEntities.product.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      onlineGames: computed(() => {
        if (like.likedEntities && !like.likedEntities['Online Game']) {
          return {};
        }
        return like.likedEntities['Online Game'].reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      cloudGame: computed(() => {
        if (like.likedEntities && !like.likedEntities['CloudGame']) {
          return {};
        }
        return like.likedEntities['CloudGame'].reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      videos: computed(() => {
        if (!like.likedEntities?.videos) {
          return {};
        }
        return like.likedEntities.videos.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      videoCollection: computed(() => {
        if (!like.likedEntities?.videoCollection) {
          return {};
        }
        return like.likedEntities.videoCollection.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      podcast: computed(() => {
        if (!like.likedEntities?.podcast) {
          return {};
        }
        return like.likedEntities.podcast.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      podcastCollection: computed(() => {
        if (!like.likedEntities?.podcastCollection) {
          return {};
        }
        return like.likedEntities.podcastCollection.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      audiobook: computed(() => {
        if (!like.likedEntities?.audiobook) {
          return {};
        }
        return like.likedEntities.audiobook.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      audiobookChapter: computed(() => {
        if (!like.likedEntities?.audiobookChapter) {
          return {};
        }
        return like.likedEntities.audiobookChapter.reduce(
          (obj: { [key: string]: boolean }, i: string) => {
            obj[i] = true;
            return obj;
          },
          {}
        );
      }),
      favs: computed(() => {
        return {
          tracks: like.tracks,
          playlists: like.playlists,
          albums: like.albums,
          artists: like.artists,
          games: like.games,
          videos: like.videos,
          'Online Game': like.onlineGames,
          CloudGame: like.cloudGame,
          videoCollection: like.videoCollection,
          podcast: like.podcast,
          podcastCollection: like.podcastCollection,
          audiobook: like.audiobook,
          product: like.product
        };
      }),

      getLikes: async (entityTrack: string): Promise<string[]> => {
        try {
          let user = $auth.user;
          if (user?.roles[0] === 'user') {
            return (await $api.endPoints.getLikedEntities(
              entityTrack
            )) as string[];
          } else {
            return [];
          }
        } catch (error) {
          return [];
        }
      },
      toggle: async (entityType: string, entity: Entity, cb: any): Promise<void> => {
        entity.entityId = entity.entityId || entity.id
        try {
          let user = $auth.user;
          if (user?.roles[0] === 'user') {
            let reqEntityType = like.entityMap[entityType];
            if (like.favs[reqEntityType][entity.entityId]) {
              await $api.endPoints.unlike(entityType, entity.entityId.toString());
              like.likedEntities[reqEntityType] =
                like.likedEntities[reqEntityType]?.filter(
                  (item: {}) => item != entity.entityId
                ) || [];
              like.removed = entity.entityId;
              if (cb) {
                cb(entity.id)
              }
              if($api?.tenant?.default?.monsooqMent){
              $snackbar.snackbar = true;
              $snackbar.requireAuth = false;
              $snackbar.text = $t('favorite.removed');
              }
              $customGtm?.track({
                EN:'game_engagement',
                AT:'Remove from Favourite',
                AI: entity?.id,
                AN: entity?.name,
                AL: entity?.licensorName,            
              });
            } else {
              const trackingData = {
                EN:'game_engagement',
                AT:'Add to Favourite',
                AI: entity?.id,
                AN: entity?.name,
                AL: entity?.licensorName,            
              };
              // Only add AN if artistName exists and is not empty
              if (entity.artistName) {
                trackingData.AN = entity.artistName;
              }
              $customGtm?.track(trackingData);

              await $api.endPoints.like(entityType, entity.entityId.toString());
              like.likedEntities[reqEntityType].push(entity.entityId as any);
              $snackbar.snackbar = true;
              $snackbar.requireAuth = false;
              $snackbar.text = $t('favorite.added');
            }
          } else {
            $snackbar.snackbar = true;
            $snackbar.requireAuth = true;
            $snackbar.text = $t('favorite.notLoggedIn');
          }
        } catch (error) {
          console.error('Error: at toggling like: ');
        }
      },
      toggleTrack: async (entity: any, cb: any): Promise<void> => {
        like.toggle('Track', entity, cb);
      },
      toggleArtist: async (entity: any, cb: any): Promise<void> => {
        like.toggle('Artist', entity, cb);
      },
      togglePlaylist: async (entity: any, cb: any): Promise<void> => {
        like.toggle('Playlist', entity, cb);
      },
      toggleAlbum: async (entity: any, cb: any): Promise<void> => {
        like.toggle('Album', entity, cb);
      },
      toggleVideo: async (entity: any, cb: any): Promise<void> => {
        like.toggle('Video', entity);
      },
      toggleVideoCollection: async (entity: any, cb: any): Promise<void> => {
        like.toggle('Video Collection', entity);
      },
      togglePodcast: async (entity: any): Promise<void> => {
        like.toggle('Podcast', entity);
      },
      togglePodcastCollection: async (entity: any): Promise<void> => {
        like.toggle('Podcast Collection', entity);
      },
      toggleAudiobookChapter: async (entity: any, cb: any): Promise<void> => {
        like.toggle('Audiobook Chapter', entity, cb);
      },
      toggleAudiobook: async (entity: any): Promise<void> => {
        like.toggle('Audiobook', entity);
      },
      toggleProduct: async (entity: any): Promise<void> => {
        like.toggle('Product', entity);
      },
      toggleGame: async (gameType: string, entity: any, cb: any): Promise<void> => {
        let type;
        if (gameType === 'ONLINE_GAME') {
          type = 'Online Game';
        } else if (gameType === 'CLOUD_GAME') {
          type = 'CloudGame';
        } else {
          type = 'Game';
        }
        like.toggle(type, entity, cb);
      },
    });
    if (import.meta.client) {
      watch(
        () => $auth?.user,
        async (value) => {
          if (process.client) {
            const [
              tracks,
              playlists,
              albums,
              artists,
              games,
              onlineGames,
              videos,
              videoCollection,
              podcast,
              podcastCollection,
              audiobook,
              audiobookChapter,
              CloudGame,
              Product
            ] = (await Promise.all([
              like.getLikes('Track'),
              like.getLikes('Playlist'),
              like.getLikes('Album'),
              like.getLikes('Artist'),
              like.getLikes('Game'),
              like.getLikes('Online Game'),
              like.getLikes('Video'),
              like.getLikes('Video Collection'),
              like.getLikes('Podcast'),
              like.getLikes('Podcast Collection'),
              like.getLikes('Audiobook'),
              like.getLikes('Audiobook Chapter'),
              like.getLikes('CloudGame'),
              like.getLikes('Product'),
            ])) as any[];
            like.likedEntities = {
              tracks,
              playlists,
              albums,
              artists,
              games,
              videos,
              'Online Game': onlineGames,
              videoCollection,
              podcast: podcast,
              podcastCollection: podcastCollection,
              audiobook,
              audiobookChapter,
              CloudGame,
              Product
            };
          }
        },
        {
          immediate: true,
        },
      );
    }
    return {
      provide: { like },
    };
  },
});
